import React from "react";
import type { DriverDeliveryCardItem } from "../../types/driverDeliveries";
import { formatMoney } from "../../utils/driverDeliveries";

type DriverDeliveriesListProps = {
  items: DriverDeliveryCardItem[];
  onSelect: (item: DriverDeliveryCardItem) => void;
  page: number;
  totalPages: number;
  pageSize: number | "all";
  onChangePage: (page: number) => void;
  onChangePageSize: (value: number | "all") => void;
};

const cardStyle: React.CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "24px",
  background: "var(--surface)",
  boxShadow: "var(--shadow)",
  padding: "18px",
  minHeight: "100%",
};

const headerRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: "12px",
  marginBottom: "14px",
};

const titleStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 800,
  color: "var(--text-primary)",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const controlsStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

const selectStyle: React.CSSProperties = {
  minHeight: "38px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  padding: "0 10px",
  fontSize: "13px",
  fontWeight: 700,
};

const listStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "12px",
};

const getLeftBorderColor = (item: DriverDeliveryCardItem): string => {
  const estado = String(item.row.estado || "").toLowerCase();

  if (estado.includes("entreg")) return "var(--success)";
  if (
    estado.includes("nadie") ||
    estado.includes("reprogram") ||
    estado.includes("demor") ||
    estado.includes("pendiente")
  ) {
    return "var(--warning)";
  }

  return "var(--primary)";
};

const itemStyle = (item: DriverDeliveryCardItem): React.CSSProperties => ({
  width: "100%",
  textAlign: "left",
  border: "1px solid var(--border)",
  borderLeft: `4px solid ${getLeftBorderColor(item)}`,
  borderRadius: "20px",
  background: "var(--surface-alt)",
  padding: "14px 16px",
  cursor: "pointer",
});

const addressStyle: React.CSSProperties = {
  fontSize: "16px",
  fontWeight: 800,
  color: "var(--text-primary)",
  lineHeight: 1.35,
};

const statusStyle: React.CSSProperties = {
  fontSize: "14px",
  color: "var(--text-secondary)",
  marginTop: "8px",
};

const sellerStyle: React.CSSProperties = {
  fontSize: "13px",
  color: "var(--text-secondary)",
};

const moneyWrapStyle: React.CSSProperties = {
  textAlign: "right",
  minWidth: "160px",
};

const amountStyle: React.CSSProperties = {
  fontSize: "18px",
  fontWeight: 900,
  color: "var(--text-primary)",
};

const paginationStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  marginTop: "16px",
  paddingTop: "14px",
  borderTop: "1px solid var(--border)",
};

const pageButtonsStyle: React.CSSProperties = {
  display: "flex",
  gap: "8px",
  alignItems: "center",
};

const pageButtonStyle: React.CSSProperties = {
  minHeight: "38px",
  minWidth: "38px",
  borderRadius: "12px",
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontWeight: 700,
  cursor: "pointer",
};

const disabledButtonStyle: React.CSSProperties = {
  ...pageButtonStyle,
  opacity: 0.45,
  cursor: "not-allowed",
};

const DriverDeliveriesList = ({
  items,
  onSelect,
  page,
  totalPages,
  pageSize,
  onChangePage,
  onChangePageSize,
}: DriverDeliveriesListProps) => {
  /**
   * Plus pausado para MVP.
   *
   * Antes se mostraba:
   * - item.bonus
   * - día penalizado
   * - copy de plus/penalización
   *
   * La data sigue existiendo en item.row para reactivarla luego.
   */

  return (
    <div style={cardStyle}>
      <div style={headerRowStyle}>
        <div>
          <div style={titleStyle}>Mis envíos</div>
          <div style={subtitleStyle}>Vista rápida de envíos y neto estimado</div>
        </div>

        <div style={controlsStyle}>
          <span style={subtitleStyle}>Mostrar</span>
          <select
            style={selectStyle}
            value={String(pageSize)}
            onChange={(e) =>
              onChangePageSize(
                e.target.value === "all" ? "all" : Number(e.target.value)
              )
            }
          >
            <option value="10">10</option>
            <option value="20">20</option>
            <option value="50">50</option>
            <option value="all">Todos</option>
          </select>
        </div>
      </div>

      <div style={listStyle}>
        {items.map((item) => (
          <button
            key={item.key}
            style={itemStyle(item)}
            onClick={() => onSelect(item)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "16px",
                alignItems: "flex-start",
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={addressStyle}>{item.direccion}</div>

                <div style={statusStyle}>{item.estado}</div>

                <div style={{ marginTop: "10px", ...sellerStyle }}>
                  {item.nombre}
                </div>
              </div>

              <div style={moneyWrapStyle}>
                <div style={amountStyle}>$ {formatMoney(item.neto)}</div>
              </div>
            </div>
          </button>
        ))}
      </div>

      {pageSize !== "all" && totalPages > 1 && (
        <div style={paginationStyle}>
          <div style={subtitleStyle}>
            Página {page} de {totalPages}
          </div>

          <div style={pageButtonsStyle}>
            <button
              style={page <= 1 ? disabledButtonStyle : pageButtonStyle}
              onClick={() => onChangePage(page - 1)}
              disabled={page <= 1}
            >
              ←
            </button>

            <button
              style={page >= totalPages ? disabledButtonStyle : pageButtonStyle}
              onClick={() => onChangePage(page + 1)}
              disabled={page >= totalPages}
            >
              →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverDeliveriesList;